import { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import PersonIcon from "@mui/icons-material/Person";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import DeleteIcon from "@mui/icons-material/Delete";
import ReceiptIcon from "@mui/icons-material/Receipt";
import DynamicGrid from "../components/DynamicTable";
import { CatalogoVentaActualizadoDialog } from "../components/CatalogoVentaActualizadoDialog";
import { ConfiguracionPresupuestoWeb, fetchConfiguracionPresupuesto, fetchDescargarPresupuestoPdf, fetchGuardarPresupuesto } from "../services/presupuestoService";
import { requestResultModel } from "../models/RequestReturnModel";
import { Producto } from "../models/ProductoModel";
import LocalSelector from "./components/LocalSelector";
import { useVentaPresupuesto } from "../hooks/useVentaPresupuesto";
import { fetchAppConfig, ProductosConfigListas } from "../services/configService";

type AjusteTipo = "ninguno" | "descuento" | "recargo";
type AjusteModo = "importe" | "porcentaje";
type ListaPrecio = { id: number; campo: "CONTADO" | "MAYOR" | "MENOR"; nombre: string };

const formasPago = ["EFECTIVO", "TARJETA", "CUENTA CORRIENTE", "CHEQUE", "BANCO", "GIFT CARD", "CREDITO PERSONAL", "PRODUCTOS"];
const moneda = new Intl.NumberFormat("es-AR", { style: "currency", currency: "ARS" });
const redondear = (value: number) => Math.round((value + Number.EPSILON) * 100) / 100;

const parseDecimalEntrada = (value: string): number | null => {
  const raw = value.trim().replace(/\s/g, "").replace(/[$]/g, "");
  if (!raw) return null;
  const comma = raw.lastIndexOf(",");
  const dot = raw.lastIndexOf(".");
  let normalized = raw;
  if (comma >= 0 && dot >= 0) {
    normalized = comma > dot ? raw.replace(/\./g, "").replace(",", ".") : raw.replace(/,/g, "");
  } else if (comma >= 0) {
    normalized = raw.replace(/\./g, "").replace(",", ".");
  }
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
};

const nombresListas = (raw: string): Record<string, string> => {
  if (!raw.trim()) return {};
  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      const result: Record<string, string> = {};
      parsed.forEach((item, index) => {
        if (typeof item === "string") {
          const key = ["CONTADO", "MAYOR", "MENOR"][index];
          if (key) result[key] = item;
        } else if (item?.campo_en_bd && item?.nombre) {
          result[String(item.campo_en_bd).toUpperCase()] = String(item.nombre);
        }
      });
      return result;
    }
    if (parsed && typeof parsed === "object") {
      return {
        CONTADO: parsed.contado ?? parsed.CONTADO ?? parsed.Contado,
        MAYOR: parsed.mayor ?? parsed.MAYOR ?? parsed.Mayor,
        MENOR: parsed.menor ?? parsed.MENOR ?? parsed.Menor,
      };
    }
  } catch {
    const values = raw.split(/[|;,]/).map((value) => value.trim());
    return { CONTADO: values[0], MAYOR: values[1], MENOR: values[2] };
  }
  return {};
};

const CantidadPresupuestoCell = ({ value, decimalPlaces, onChange }: { value: number; decimalPlaces: number; onChange: (value: number) => void }) => {
  const [draft, setDraft] = useState(String(value));
  useEffect(() => setDraft(String(value)), [value]);

  return (
    <TextField
      size="small"
      value={draft}
      inputProps={{ inputMode: "decimal", "aria-label": "Cantidad" }}
      onKeyDown={(event) => event.stopPropagation()}
      onChange={(event) => {
        const raw = event.target.value;
        if (!/^\d*(?:[.,]\d*)?$/.test(raw)) return;
        const separatorIndex = Math.max(raw.lastIndexOf("."), raw.lastIndexOf(","));
        const decimalDigits = separatorIndex >= 0 ? raw.length - separatorIndex - 1 : 0;
        if ((decimalPlaces === 0 && separatorIndex >= 0) || decimalDigits > decimalPlaces) return;
        setDraft(raw);
        if (!raw || raw.endsWith(".") || raw.endsWith(",")) return;
        const parsed = Number(raw.replace(",", "."));
        if (Number.isFinite(parsed)) onChange(parsed);
      }}
      onBlur={() => {
        if (!draft || draft.endsWith(".") || draft.endsWith(",")) setDraft(String(value));
      }}
      sx={{ width: 110, "& input": { textAlign: "right", py: 0.75 } }}
    />
  );
};

export const gridColumnDefinitions = {
  productos: {
    PRODUCTO: { Header: "Producto", Editable: false },
    CODIGO: { Header: "Código", Editable: false },
    CONTADO: { Header: "$ Unitario", Editable: false, IsNumber: true },
  },
  clientes: { CLIENTE: { Header: "Cliente", Editable: false } },
};

const Presupuesto = () => {
  const {
    idLocal, selectDialogOpen, clienteSeleccionado, lineaVenta, productos, productosRevision,
    setLineaVenta, data, seleccionandoCliente, loadingProductos, userLoggedIn,
    handleSelectClienteDialog, handleSelectProductoDialog, eventHandler, handleDeleteRow,
    handleLocalChange, resetForm, showMessage, setSelectDialogOpen,
  } = useVentaPresupuesto();

  const [plazo, setPlazo] = useState("30");
  const [formaPago, setFormaPago] = useState("EFECTIVO");
  const [idLista, setIdLista] = useState(1);
  const [ajusteTipo, setAjusteTipo] = useState<AjusteTipo>("ninguno");
  const [ajusteModo, setAjusteModo] = useState<AjusteModo>("importe");
  const [ajusteValor, setAjusteValor] = useState("0");
  const [senia, setSenia] = useState("0");
  const [guardando, setGuardando] = useState(false);
  const [configuracionWeb, setConfiguracionWeb] = useState<ConfiguracionPresupuestoWeb | null>(null);
  const [configuracionListas, setConfiguracionListas] = useState<ProductosConfigListas | undefined>();
  const [ultimoPresupuesto, setUltimoPresupuesto] = useState<{ idLocal: number; idPresupuesto: number; permiteFotos: boolean } | null>(null);
  const [incluirFotos, setIncluirFotos] = useState(true);
  const [descargando, setDescargando] = useState(false);
  const [decimalesEnCantidad, setDecimalesEnCantidad] = useState(2);
  const guardandoRef = useRef(false);

  useEffect(() => {
    setConfiguracionWeb(null);
    if (!userLoggedIn || !idLocal || isNaN(Number(idLocal))) return;
    fetchConfiguracionPresupuesto(userLoggedIn, Number(idLocal))
      .then(setConfiguracionWeb)
      .catch(() => setConfiguracionWeb({ habilitado: false, pv: null, permiteFotos: false, mensaje: "No se pudo verificar la configuración. Vuelva a intentar." }));
  }, [idLocal, userLoggedIn]);

  useEffect(() => {
    let cancelled = false;
    const pv = configuracionWeb?.habilitado ? configuracionWeb.pv : null;
    if (!userLoggedIn || !idLocal || !pv || pv <= 0) {
      setDecimalesEnCantidad(2);
      setConfiguracionListas(undefined);
      return () => { cancelled = true; };
    }

    fetchAppConfig(userLoggedIn, Number(idLocal), pv)
      .then((config) => {
        if (cancelled) return;
        const configuredDecimals = Math.trunc(config?.decimalesEnCantidad ?? 2);
        setDecimalesEnCantidad(Math.max(0, Math.min(6, configuredDecimals)));
        setConfiguracionListas(config);
      })
      .catch(() => {
        if (!cancelled) {
          setDecimalesEnCantidad(2);
          setConfiguracionListas(undefined);
        }
      });

    return () => { cancelled = true; };
  }, [configuracionWeb, idLocal, userLoggedIn]);

  const listas = useMemo<ListaPrecio[]>(() => {
    const nombres = nombresListas(configuracionListas?.nombreListas ?? "");
    return [
      { id: 1, campo: "CONTADO", nombre: nombres.CONTADO ?? "Contado" },
      { id: 2, campo: "MAYOR", nombre: nombres.MAYOR ?? "Mayorista" },
      { id: 3, campo: "MENOR", nombre: nombres.MENOR ?? "Minorista" },
    ];
  }, [configuracionListas]);

  const obtenerPrecio = (producto: Producto) => {
    const lista = listas.find((item) => item.id === idLista) ?? listas[0];
    const raw = (producto as any)[lista.campo] ?? (lista.id === 1 ? producto.precio : undefined) ?? producto.CONTADO ?? 0;
    return parseDecimalEntrada(String(raw)) ?? 0;
  };

  const subtotal = redondear(lineaVenta.reduce((sum, linea) => sum + linea.cantidad * obtenerPrecio(linea.producto), 0));
  const parsedAjuste = parseDecimalEntrada(ajusteValor);
  const parsedSenia = parseDecimalEntrada(senia);
  const porcentajeInvalido = ajusteTipo !== "ninguno" && (parsedAjuste === null || parsedAjuste < 0);
  const ajusteImporte = ajusteTipo === "ninguno" || parsedAjuste === null
    ? 0
    : redondear(ajusteModo === "porcentaje" ? subtotal * parsedAjuste / 100 : parsedAjuste);
  const descuento = ajusteTipo === "descuento" ? ajusteImporte : 0;
  const recargo = ajusteTipo === "recargo" ? ajusteImporte : 0;
  const total = redondear(subtotal - descuento + recargo);
  const saldo = redondear(total - (parsedSenia ?? 0));
  const plazoNumero = Number(plazo);
  const errores = {
    plazo: !Number.isInteger(plazoNumero) || plazoNumero <= 0,
    ajuste: porcentajeInvalido || (ajusteTipo === "descuento" && ajusteImporte > subtotal),
    senia: parsedSenia === null || parsedSenia < 0 || parsedSenia > total,
  };
  const presupuestoValido = Boolean(
    configuracionWeb?.habilitado && clienteSeleccionado && lineaVenta.length > 0 && formaPago
    && !errores.plazo && !errores.ajuste && !errores.senia
    && lineaVenta.every((linea) => Number.isFinite(linea.cantidad) && linea.cantidad > 0 && obtenerPrecio(linea.producto) >= 0)
  );

  const guardarPresupuesto = async () => {
    if (guardandoRef.current || !userLoggedIn || !clienteSeleccionado || !idLocal || !presupuestoValido) return;
    guardandoRef.current = true;
    setGuardando(true);
    try {
      const res = await fetchGuardarPresupuesto(userLoggedIn, {
        idCliente: clienteSeleccionado.ID,
        idLocal: Number(idLocal),
        formaPago,
        plazo: plazoNumero,
        descuento,
        recargo,
        senia: parsedSenia ?? 0,
        idLista,
        total,
        lineas: lineaVenta.map((linea) => ({
          idProducto: linea.producto.IDPRODUCTO,
          codigo: linea.producto.CODIGO,
          producto: linea.producto.PRODUCTO,
          cantidad: linea.cantidad,
          precio: obtenerPrecio(linea.producto),
        })),
      });
      showMessage(res.message, res.success ? "success" : "error");
      if (!res.success) return;
      const respuesta = res as requestResultModel & { idPresupuesto?: number };
      const idPresupuesto = Number(respuesta.idPresupuesto ?? respuesta.data);
      if (idPresupuesto > 0) setUltimoPresupuesto({ idLocal: Number(idLocal), idPresupuesto, permiteFotos: configuracionWeb!.permiteFotos });
      else showMessage("El presupuesto se guardó, pero no se recibió su identificador para habilitar el PDF.", "warning");
      setIncluirFotos(configuracionWeb!.permiteFotos);
      resetForm();
      setPlazo("30");
      setFormaPago("EFECTIVO");
      setIdLista(1);
      setAjusteTipo("ninguno");
      setAjusteModo("importe");
      setAjusteValor("0");
      setSenia("0");
    } catch (error) {
      showMessage(error instanceof Error ? error.message : "Error al guardar el presupuesto", "error");
    } finally {
      guardandoRef.current = false;
      setGuardando(false);
    }
  };

  const descargarPdf = async () => {
    if (!userLoggedIn || !ultimoPresupuesto || descargando) return;
    setDescargando(true);
    try {
      const blob = await fetchDescargarPresupuestoPdf(userLoggedIn, ultimoPresupuesto.idLocal, ultimoPresupuesto.idPresupuesto, ultimoPresupuesto.permiteFotos && incluirFotos);
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `Presupuesto_${ultimoPresupuesto.idPresupuesto}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      showMessage(error instanceof Error ? error.message : "No se pudo generar el PDF. Puede volver a intentarlo.", "error");
    } finally {
      setDescargando(false);
    }
  };

  const lineasGrid = lineaVenta.map((linea, index) => ({
    id: index,
    producto: linea.producto.PRODUCTO,
    precio: obtenerPrecio(linea.producto),
    cantidad: linea.cantidad,
    importe: redondear(obtenerPrecio(linea.producto) * linea.cantidad),
  }));
  const columnas: GridColDef[] = [
    { field: "producto", headerName: "Producto", minWidth: 220, flex: 1 },
    { field: "precio", headerName: "Precio unitario", width: 150, align: "right", headerAlign: "right", valueFormatter: (value) => moneda.format(Number(value)) },
    {
      field: "cantidad", headerName: "Cantidad", width: 150, sortable: false, align: "right", headerAlign: "right",
      renderCell: (params) => <CantidadPresupuestoCell value={Number(params.value)} decimalPlaces={decimalesEnCantidad}
        onChange={(cantidad) => setLineaVenta((actuales) => actuales.map((linea, index) => index === Number(params.id) ? { ...linea, cantidad } : linea))} />,
    },
    { field: "importe", headerName: "Total línea", width: 155, align: "right", headerAlign: "right", valueFormatter: (value) => moneda.format(Number(value)) },
    { field: "eliminar", headerName: "", width: 88, sortable: false, filterable: false, align: "center", renderCell: (params) => <Button aria-label="Eliminar producto" color="error" onClick={() => handleDeleteRow(Number(params.id))}><DeleteIcon /></Button> },
  ];

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2.5 }, pb: { xs: 25, md: 16 } }}>
      <CatalogoVentaActualizadoDialog productos={productos} revision={productosRevision} lineas={lineaVenta} setLineas={setLineaVenta} />
      <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "stretch", sm: "center" }} spacing={1} sx={{ mb: 2 }}>
        <Typography variant="h5" fontWeight={700}>Nuevo presupuesto</Typography>
        <Box sx={{ width: { xs: "100%", sm: 220 } }}><LocalSelector onLocalChange={handleLocalChange} /></Box>
      </Stack>

      {configuracionWeb && !configuracionWeb.habilitado && <Alert severity="warning" sx={{ mb: 2 }}>{configuracionWeb.mensaje}</Alert>}
      <Alert severity="info" sx={{ mb: 2 }}>
        Elegí la lista que define los precios. El descuento reduce el subtotal; el recargo lo aumenta. La seña se resta para mostrar el saldo pendiente. La fecha se asigna en el servidor.
      </Alert>

      <Paper variant="outlined" sx={{ p: { xs: 1.5, sm: 2 }, mb: 2 }}>
        <Grid container spacing={1.5} alignItems="center">
          <Grid item xs={12} md={4}>
            <Typography variant="subtitle2" color="text.secondary">Cliente</Typography>
            <Typography fontWeight={700} noWrap>{clienteSeleccionado?.CLIENTE || "Seleccione un cliente"}</Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Button onClick={handleSelectClienteDialog} disabled={!configuracionWeb?.habilitado} variant="outlined" fullWidth startIcon={<PersonIcon />}>Seleccionar cliente</Button>
          </Grid>
        </Grid>
      </Paper>

      <Paper variant="outlined" sx={{ p: { xs: 1.5, sm: 2 }, mb: 2 }}>
        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5 }}>Condiciones comerciales</Typography>
        <Grid container spacing={1.5}>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small" disabled={!configuracionWeb?.habilitado}>
              <InputLabel id="lista-precio-label">Lista de precios</InputLabel>
              <Select labelId="lista-precio-label" label="Lista de precios" value={idLista} onChange={(event) => setIdLista(Number(event.target.value))}>
                {listas.map((lista) => <MenuItem key={lista.id} value={lista.id}>{lista.nombre}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small" disabled={!configuracionWeb?.habilitado}>
              <InputLabel id="forma-pago-label">Forma de pago</InputLabel>
              <Select labelId="forma-pago-label" label="Forma de pago" value={formaPago} onChange={(event) => setFormaPago(event.target.value)}>
                {formasPago.map((forma) => <MenuItem key={forma} value={forma}>{forma}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField fullWidth size="small" label="Validez (días)" type="number" value={plazo} onChange={(event) => setPlazo(event.target.value)} error={errores.plazo} helperText={errores.plazo ? "Ingresá días enteros mayores a cero." : " "} inputProps={{ min: 1, step: 1 }} disabled={!configuracionWeb?.habilitado} />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small" disabled={!configuracionWeb?.habilitado}>
              <InputLabel id="ajuste-tipo-label">Ajuste</InputLabel>
              <Select labelId="ajuste-tipo-label" label="Ajuste" value={ajusteTipo} onChange={(event) => setAjusteTipo(event.target.value as AjusteTipo)}>
                <MenuItem value="ninguno">Sin ajuste</MenuItem><MenuItem value="descuento">Descuento</MenuItem><MenuItem value="recargo">Recargo</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={3} md={1}>
            <FormControl fullWidth size="small" disabled={ajusteTipo === "ninguno" || !configuracionWeb?.habilitado}>
              <InputLabel id="ajuste-modo-label">Tipo</InputLabel>
              <Select labelId="ajuste-modo-label" label="Tipo" value={ajusteModo} onChange={(event) => setAjusteModo(event.target.value as AjusteModo)}>
                <MenuItem value="importe">$</MenuItem><MenuItem value="porcentaje">%</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={3} md={1}>
            <TextField fullWidth size="small" label={ajusteModo === "importe" ? "Importe" : "%"} value={ajusteValor} onChange={(event) => setAjusteValor(event.target.value)} disabled={ajusteTipo === "ninguno" || !configuracionWeb?.habilitado} error={errores.ajuste} inputProps={{ inputMode: "decimal" }} />
          </Grid>
          {ajusteTipo !== "ninguno" && <Grid item xs={12}>
            <Typography variant="caption" color={errores.ajuste ? "error" : "text.secondary"}>
              {errores.ajuste ? (ajusteImporte > subtotal && ajusteTipo === "descuento" ? "El descuento no puede superar el subtotal." : "Ingresá un importe o porcentaje válido y no negativo.") : `Equivale a ${moneda.format(ajusteImporte)}.`}
            </Typography>
          </Grid>}
        </Grid>
      </Paper>

      <Paper variant="outlined" sx={{ p: { xs: 1, sm: 1.5 } }}>
        <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "stretch", sm: "center" }} spacing={1} sx={{ px: 0.5, mb: 1 }}>
          <Typography variant="subtitle1" fontWeight={700}>Productos ({lineaVenta.length})</Typography>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ xs: "stretch", sm: "center" }}>
            {lineaVenta.length > 0 && <Typography variant="body2" color="text.secondary">Lista: {listas.find((lista) => lista.id === idLista)?.nombre}</Typography>}
            <Button onClick={handleSelectProductoDialog} variant="contained" color="secondary" disabled={loadingProductos || !configuracionWeb?.habilitado} startIcon={loadingProductos ? <CircularProgress size={18} /> : <ShoppingCartIcon />}>
              {loadingProductos ? "Cargando productos..." : "Agregar productos"}
            </Button>
          </Stack>
        </Stack>
        {lineaVenta.length === 0 ? (
          <Alert severity="info" sx={{ minHeight: 72, alignItems: "center" }}>Todavía no hay productos. Usá “Agregar productos” para comenzar.</Alert>
        ) : (
          <Box sx={{ width: "100%", height: Math.min(420, Math.max(116, lineasGrid.length * 60 + 56)), overflow: "hidden" }}>
            <DataGrid rows={lineasGrid} columns={columnas} disableRowSelectionOnClick hideFooter density="compact" rowHeight={60} columnHeaderHeight={48}
              sx={{ border: 0, "& .MuiDataGrid-cell:focus, & .MuiDataGrid-columnHeader:focus": { outline: "none" } }} />
          </Box>
        )}
      </Paper>

      <Dialog open={selectDialogOpen} fullWidth maxWidth="lg" keepMounted onClose={() => setSelectDialogOpen(false)}>
        <Typography variant="h6" sx={{ p: 2 }}>{seleccionandoCliente ? "Seleccionar cliente" : "Seleccionar producto"}</Typography>
        <Box sx={{ p: 1 }}>
          <DynamicGrid data={data} customHeaderMap={seleccionandoCliente ? gridColumnDefinitions.clientes : gridColumnDefinitions.productos}
            pageSize={5} removeIfNotCustom rowClickEventHandler={eventHandler} />
        </Box>
      </Dialog>

      <Paper elevation={10} sx={{
        position: "fixed", bottom: 0, left: { xs: 0, md: "calc(var(--app-menu-width, 0px) + 12px)" }, right: 0,
        zIndex: (theme) => theme.zIndex.appBar - 1, px: { xs: 1, sm: 2 }, py: 1,
        borderTop: "1px solid", borderColor: "divider", borderRadius: 0,
      }}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={1.25} alignItems={{ xs: "stretch", md: "center" }} justifyContent="space-between">
          <Stack direction="row" spacing={{ xs: 1, sm: 2.5 }} useFlexGap flexWrap="wrap" justifyContent="space-between">
            <Box><Typography variant="caption" color="text.secondary">Subtotal</Typography><Typography fontWeight={600}>{moneda.format(subtotal)}</Typography></Box>
            {descuento > 0 && <Box><Typography variant="caption" color="text.secondary">Descuento</Typography><Typography color="error.main">− {moneda.format(descuento)}</Typography></Box>}
            {recargo > 0 && <Box><Typography variant="caption" color="text.secondary">Recargo</Typography><Typography color="warning.dark">+ {moneda.format(recargo)}</Typography></Box>}
            <Box><Typography variant="caption" color="text.secondary">Total</Typography><Typography variant="h6" fontWeight={800} lineHeight={1.2}>{moneda.format(total)}</Typography></Box>
            <Box sx={{ width: { xs: 116, sm: 140 } }}>
              <TextField size="small" label="Seña" value={senia} onChange={(event) => setSenia(event.target.value)} error={errores.senia} helperText={errores.senia ? "Debe estar entre $0 y el total." : " "} inputProps={{ inputMode: "decimal" }} disabled={!configuracionWeb?.habilitado} />
            </Box>
            <Box><Typography variant="caption" color="text.secondary">Saldo</Typography><Typography fontWeight={700}>{moneda.format(saldo)}</Typography></Box>
          </Stack>
          <Button onClick={guardarPresupuesto} disabled={guardando || !presupuestoValido} variant="contained" color="success" size="large" startIcon={guardando ? <CircularProgress color="inherit" size={18} /> : <ReceiptIcon />} sx={{ minWidth: { md: 220 }, fontWeight: 700, flexShrink: 0 }}>
            {guardando ? "Guardando..." : "Guardar presupuesto"}
          </Button>
        </Stack>
      </Paper>

      {ultimoPresupuesto && (
        <Paper variant="outlined" sx={{ mt: 2, p: 2 }}>
          <Stack direction={{ xs: "column", sm: "row" }} alignItems={{ xs: "stretch", sm: "center" }} spacing={1}>
            <Typography sx={{ flexGrow: 1 }}>Presupuesto #{ultimoPresupuesto.idPresupuesto} guardado</Typography>
            {ultimoPresupuesto.permiteFotos && <FormControlLabel control={<Checkbox checked={incluirFotos} onChange={(event) => setIncluirFotos(event.target.checked)} />} label="Incluir fotos" />}
            <Button variant="outlined" onClick={descargarPdf} disabled={descargando} startIcon={descargando ? <CircularProgress size={18} /> : undefined}>
              {descargando ? "Generando PDF..." : "Descargar PDF"}
            </Button>
          </Stack>
        </Paper>
      )}
    </Box>
  );
};

export default Presupuesto;
